import React, { useRef, useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import emailjs from "@emailjs/browser";
import { Snackbar, IconButton } from "@mui/material";
import {
  CloseRounded,
  SendRounded,
  EmailRounded,
  PersonRounded,
  SubjectRounded,
  MessageRounded,
} from "@mui/icons-material";

// Animations
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(40px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 5px 20px rgba(133, 76, 230, 0.3); }
  50% { transform: scale(1.05); box-shadow: 0 8px 30px rgba(133, 76, 230, 0.5); }
  100% { transform: scale(1); box-shadow: 0 5px 20px rgba(133, 76, 230, 0.3); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 40px 20px;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.bg} 0%,
    ${({ theme }) => theme.card_light}15 100%
  );
  overflow: hidden;

  @media (max-width: 960px) {
    padding: 10px 16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  gap: 32px;
  animation: ${fadeInUp} 0.8s ease;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 800;
  text-align: center;
  background: linear-gradient(135deg, #854ce6, #5ab2ff, #854ce6);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientShift} 4s ease infinite;
  margin: 0;
  padding-bottom: 20px;
  position: relative;
  letter-spacing: 0.5px;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #854ce6, #5ab2ff);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    padding-bottom: 16px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  color: ${({ theme }) => theme.text_secondary};
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactForm = styled.form`
  width: 100%;
  background: ${({ theme }) => theme.card};
  padding: 40px;
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(133, 76, 230, 0.1);
  display: flex;
  flex-direction: column;
  gap: 24px;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #854ce6, #5ab2ff);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }

  &:hover:before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 45px rgba(133, 76, 230, 0.2),
      0 0 0 1px rgba(133, 76, 230, 0.15);
  }

  @media (max-width: 768px) {
    padding: 30px 24px;
    gap: 20px;
  }
`;

const ContactTitle = styled.h3`
  font-size: 28px;
  margin-bottom: 8px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  align-items: center;
  gap: 12px;

  &:before {
    content: "📨";
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.text_secondary};
  transition: color 0.3s ease;
`;

const ContactInput = styled.input`
  width: 100%;
  background-color: ${({ theme }) => theme.bgLight};
  border: 1.5px solid ${({ theme }) => theme.text_secondary + 40};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 16px;
  padding: 16px 16px 16px 50px;
  transition: all 0.3s ease;
  font-weight: 500;

  &:focus {
    border: 1.5px solid #854ce6;
    box-shadow: 0 0 0 3px rgba(133, 76, 230, 0.1);

    ~ ${InputIcon} {
      color: #854ce6;
    }
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + 80};
  }
`;

const ContactTextArea = styled.textarea`
  width: 100%;
  background-color: ${({ theme }) => theme.bgLight};
  border: 1.5px solid ${({ theme }) => theme.text_secondary + 40};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 16px;
  padding: 16px 16px 16px 50px;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 120px;
  font-weight: 500;
  font-family: inherit;

  &:focus {
    border: 1.5px solid #854ce6;
    box-shadow: 0 0 0 3px rgba(133, 76, 230, 0.1);

    ~ ${InputIcon} {
      color: #854ce6;
    }
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + 80};
  }
`;

const ContactButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #854ce6, #5ab2ff);
  background-size: 200% 200%;
  padding: 18px 24px;
  border-radius: 16px;
  border: none;
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
  animation: ${pulse} 3s ease infinite;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    animation: none;
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(133, 76, 230, 0.4);
    background-position: 100% 50%;

    &:before {
      left: 100%;
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    animation: none;
  }

  ${(props) =>
    props.isLoading &&
    css`
      animation: ${pulse} 1s ease infinite;
    `}

  ${(props) =>
    props.isError &&
    css`
      animation: ${shake} 0.5s ease;
      background: linear-gradient(135deg, #ff4757, #ff6b81);
    `}
`;

const DecorativeElements = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
`;

const FloatingElement = styled.div`
  position: absolute;
  width: ${(props) => props.size || "150px"};
  height: ${(props) => props.size || "150px"};
  background: ${(props) => props.color || "rgba(133, 76, 230, 0.08)"};
  border-radius: 50%;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || "0s"};
  filter: blur(30px);
  top: ${(props) => props.top || "20%"};
  left: ${(props) => props.left || "10%"};
  opacity: 0.6;
`;

const SuccessMessage = styled.div`
  background: linear-gradient(
    135deg,
    rgba(46, 204, 113, 0.1),
    rgba(46, 204, 113, 0.05)
  );
  border: 1px solid rgba(46, 204, 113, 0.2);
  color: #2ecc71;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  font-weight: 500;
  margin-top: 16px;
  animation: ${fadeInUp} 0.5s ease;
`;

const ErrorMessage = styled.div`
  background: linear-gradient(
    135deg,
    rgba(231, 76, 60, 0.1),
    rgba(231, 76, 60, 0.05)
  );
  border: 1px solid rgba(231, 76, 60, 0.2);
  color: #e74c3c;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  font-weight: 500;
  margin-top: 16px;
  animation: ${fadeInUp} 0.5s ease;
`;

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const form = useRef();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 100);

    // Initialize EmailJS with your Public Key
    emailjs.init("_3Oao5qDNkLyuLZ5o"); // Replace with your actual public key

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsError(false);
    setIsSubmitted(false);
    setErrorMsg("");

    // Basic form validation
    const formData = new FormData(form.current);
    const from_name = formData.get("from_name");
    const from_email = formData.get("from_email");
    const subject = formData.get("subject");
    const message = formData.get("message");

    if (!from_name || !from_email || !subject || !message) {
      setIsError(true);
      setErrorMsg("Please fill in all fields");
      setIsLoading(false);

      setTimeout(() => {
        setIsError(false);
        setErrorMsg("");
      }, 3000);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(from_email)) {
      setIsError(true);
      setErrorMsg("Please enter a valid email address");
      setIsLoading(false);

      setTimeout(() => {
        setIsError(false);
        setErrorMsg("");
      }, 3000);
      return;
    }

    try {
      // Send email using EmailJS
      const result = await emailjs.sendForm(
        "service_qwt8dl8", // Replace with your Service ID
        "template_gowx6fp", // Replace with your Template ID
        form.current,
        "_3Oao5qDNkLyuLZ5o" // Replace with your Public Key
      );

      console.log("Email sent successfully:", result);

      if (result.status === 200) {
        setOpen(true);
        setIsSubmitted(true);
        form.current.reset();

        // Reset success state after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        throw new Error("Email service returned non-200 status");
      }
    } catch (error) {
      console.error("Email sending failed:", error);
      setIsError(true);
      setErrorMsg("Failed to send message. Please try again later.");

      setTimeout(() => {
        setIsError(false);
        setErrorMsg("");
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container id="contact">
      <DecorativeElements>
        <FloatingElement
          size="120px"
          color="rgba(133, 76, 230, 0.1)"
          top="15%"
          left="5%"
          delay="0s"
        />
        <FloatingElement
          size="180px"
          color="rgba(90, 178, 255, 0.08)"
          top="70%"
          left="85%"
          delay="2s"
        />
        <FloatingElement
          size="100px"
          color="rgba(133, 76, 230, 0.06)"
          top="40%"
          left="75%"
          delay="4s"
        />
      </DecorativeElements>

      <Wrapper style={{ opacity: animated ? 1 : 0 }}>
        <Title>Get In Touch</Title>
        <Desc>
          I'm always open to discussing new opportunities, creative projects, or
          just having a friendly chat. Let's create something amazing together!
          ✨
        </Desc>

        <ContactForm ref={form} onSubmit={handleSubmit}>
          <ContactTitle>Send me a message</ContactTitle>

          <InputContainer>
            <InputIcon>
              <PersonRounded />
            </InputIcon>
            <ContactInput
              placeholder="Your Name"
              name="from_name"
              required
              type="text"
            />
          </InputContainer>

          <InputContainer>
            <InputIcon>
              <EmailRounded />
            </InputIcon>
            <ContactInput
              placeholder="Your Email"
              name="from_email"
              required
              type="email"
            />
          </InputContainer>

          <InputContainer>
            <InputIcon>
              <SubjectRounded />
            </InputIcon>
            <ContactInput
              placeholder="Subject"
              name="subject"
              required
              type="text"
            />
          </InputContainer>

          <InputContainer>
            <InputIcon style={{ top: "20px", transform: "none" }}>
              <MessageRounded />
            </InputIcon>
            <ContactTextArea
              placeholder="Your Message..."
              rows="5"
              name="message"
              required
            />
          </InputContainer>

          <ContactButton
            type="submit"
            isLoading={isLoading}
            isError={isError}
            disabled={isLoading}
          >
            {isLoading ? (
              <>Sending...</>
            ) : isError ? (
              <>Try Again</>
            ) : (
              <>
                <SendRounded /> Send Message
              </>
            )}
          </ContactButton>

          {isError && errorMsg && (
            <ErrorMessage>
              ❌ {errorMsg}
            </ErrorMessage>
            )}

          {isSubmitted && (
            <SuccessMessage>
              ✅ Message sent successfully! I'll get back to you soon.
            </SuccessMessage>
          )}
        </ContactForm>

        {/* <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={() => setOpen(false)}
          message="Message sent successfully!"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setOpen(false)}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          }
        /> */}
      </Wrapper>
    </Container>
  );
};

export default Contact;
